import React, { useCallback, useContext, useRef, useState } from 'react';
import CommonLayout from '../components/CommonLayout';
import AppConfigSettings from '../components/AppConfigSettings';
import WikiRebuildSettings from '../components/WikiRebuildSettings';
import AuditSettings from '../components/AuditSettings';
import CrmSettings from '../components/CrmSettings';
import SecurityPolicySettings from '../components/SecurityPolicySettings';
import EntraMigrationSettings from '../components/EntraMigrationSettings';
import { LeaveGuard } from '../hooks/useUnsavedChangesGuard';
import { UserContext } from '../context/UserContext';
import { AI_CHAT_ENABLED } from '../services/FeatureFlagService';
import './Settings.scss';

const Settings: React.FC = () => {
  const { user } = useContext(UserContext);
  const isSuperAdmin = Boolean(user && user.isSuperAdmin);

  const [activeTab, setActiveTab] = useState<string>('appConfigs');

  // A tab with unsaved changes (security policies) registers a guard here, so
  // switching tabs asks first instead of silently dropping them.
  const leaveGuardRef = useRef<LeaveGuard | null>(null);
  const registerLeaveGuard = useCallback((guard: LeaveGuard | null) => {
    leaveGuardRef.current = guard;
  }, []);
  const switchTab = (tab: string) => {
    if (tab === activeTab) return;
    const guard = leaveGuardRef.current;
    if (guard && guard(() => setActiveTab(tab))) return;
    setActiveTab(tab);
  };

  // Tabs for different settings sections
  const renderTabs = () => {
    return (
      <div className="settings-tabs">
        <button
          className={`tab-button ${activeTab === 'appConfigs' ? 'active' : ''}`}
          onClick={() => switchTab('appConfigs')}
        >
          הגדרות אפליקציות
        </button>
        {isSuperAdmin && AI_CHAT_ENABLED && (
          <button
            className={`tab-button ${activeTab === 'wikiRebuild' ? 'active' : ''}`}
            onClick={() => switchTab('wikiRebuild')}
          >
            אינדקס פרוטוקולים (AI)
          </button>
        )}
        {isSuperAdmin && (
          <button
            className={`tab-button ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => switchTab('audit')}
          >
            יומן ביקורת
          </button>
        )}
        {isSuperAdmin && (
          <button
            className={`tab-button ${activeTab === 'crm' ? 'active' : ''}`}
            onClick={() => switchTab('crm')}
          >
            קשרי לקוחות
          </button>
        )}
        {isSuperAdmin && (
          <button
            className={`tab-button ${activeTab === 'securityPolicies' ? 'active' : ''}`}
            onClick={() => switchTab('securityPolicies')}
          >
            מדיניות אבטחה
          </button>
        )}
        {isSuperAdmin && (
          <button
            className={`tab-button ${activeTab === 'entraMigration' ? 'active' : ''}`}
            onClick={() => switchTab('entraMigration')}
          >
            מעבר לכניסה החדשה
          </button>
        )}
        {/* Additional tabs can be added here */}
      </div>
    );
  };

  // Content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'appConfigs':
        return <AppConfigSettings />;
      case 'wikiRebuild':
        return isSuperAdmin ? <WikiRebuildSettings /> : <AppConfigSettings />;
      case 'audit':
        return isSuperAdmin ? <AuditSettings /> : <AppConfigSettings />;
      case 'crm':
        return isSuperAdmin ? <CrmSettings /> : <AppConfigSettings />;
      case 'securityPolicies':
        return isSuperAdmin ? <SecurityPolicySettings onRegisterLeaveGuard={registerLeaveGuard} /> : <AppConfigSettings />;
      case 'entraMigration':
        return isSuperAdmin ? <EntraMigrationSettings /> : <AppConfigSettings />;
      // Add cases for additional tabs here
      default:
        return <AppConfigSettings />;
    }
  };

  return (
    // Wider than the 1000px default: the admin tables (audit log, customer
    // relations) are many columns wide. Scoped to this page only — CommonLayout
    // takes the width per screen.
    <CommonLayout maxWidth="1600px">
      <div className="settings-container" dir="rtl">
        <h1 className="settings-title">הגדרות מערכת</h1>
        
        {renderTabs()}
        {renderContent()}
      </div>
    </CommonLayout>
  );
};

export default Settings;